import { useMemo } from 'react';
import { useAuthoritativeTradesRecords } from './useAuthoritativeTrades';
import { filterCanonicalDisplayTrades } from '../lib/trades/dashboardTradeSource';
import { currentRuntime } from '../config/runtime';
import type { TenantId } from '../config/runtime/types';

/**
 * The SINGLE canonical filtered Trade[] collection for every statistics surface.
 *
 * Every realized-statistics consumer (Dashboard, Trades page totals, Calendar,
 * Journal, Trend, WinVsLoss, and every performance widget) MUST derive from this
 * same collection so a tenant can never observe different totals on different
 * pages. It is the canonical hook output (imported + server-persisted + custom
 * trades, tenant asset policy applied, deduplicated, normalized) plus the shared
 * canonical display filter that drops invalid/rejected import rows.
 *
 * No page or widget may compute realized statistics from a differently-filtered
 * copy (e.g. raw `loadTrades()` or the unfiltered canonical hook).
 */
export function useCanonicalStatisticsTrades(tenantId: TenantId = currentRuntime.tenant.id) {
  const authoritativeTrades = useAuthoritativeTradesRecords();

  return useMemo(
    () => filterCanonicalDisplayTrades(authoritativeTrades as any[], tenantId),
    [authoritativeTrades, tenantId]
  );
}
